import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { connectDrupalSourceOfTruthCerner } from '~/platform/utilities/cerner/dsot';
import { useFeatureToggle } from '~/platform/utilities/feature-toggles/useFeatureToggle';
import { selectBlockedFromOhOnly } from '../util/oracleHealthAccess';

/**
 * Enforces the single OH access rule (#150770).
 *
 * Gated by the `mhv_block_oh_only_no_correlation` feature flag; when the flag
 * is off the hook always returns false and skips loading the Drupal data. When
 * on, it loads the Drupal vamc-ehr source of truth (so each facility's EHR type
 * is known), then returns whether the current user is an "Oracle Health only,
 * no correlation" user who must be shown the non-patient landing page instead
 * of the MHV tools. Fails open: returns false while profile facilities or
 * Drupal EHR data are still loading.
 *
 * @param {Object} [options]
 * @param {boolean} [options.redirect=false] when true, sends a blocked user to
 *   the MHV landing page, which shows them the non-patient view
 * @returns {boolean} true when the user should be blocked
 */
export default function useBlockedFromOhOnly({ redirect = false } = {}) {
  const dispatch = useDispatch();
  const { useToggleValue, TOGGLE_NAMES } = useFeatureToggle();
  const featureEnabled = useToggleValue(
    TOGGLE_NAMES.mhvBlockOhOnlyNoCorrelation,
  );

  useEffect(() => {
    // Load Drupal Cerner (vamc-ehr) data so the OH rule can evaluate.
    if (featureEnabled) {
      connectDrupalSourceOfTruthCerner(dispatch);
    }
  }, [dispatch, featureEnabled]);

  const blockedFromOhOnly = useSelector(selectBlockedFromOhOnly);
  const blocked = Boolean(featureEnabled && blockedFromOhOnly);

  useEffect(() => {
    if (redirect && blocked) {
      window.location.replace('/my-health');
    }
  }, [redirect, blocked]);

  return blocked;
}
